/** Types for the `/api/v1` Reporting API client (Reports tab only). */

export type ReportingApiDataset = 'performance' | 'geo' | 'domain' | 'conversions';

export interface ReportingApiSuccessEnvelope<T> {
  data: T;
  meta?: {
    request_id?: string;
    columns?: string[];
    date_range?: { start_date?: string; end_date?: string };
    row_count?: number;
    cached?: boolean;
    data_version?: string;
  };
  page?: {
    limit?: number;
    next_cursor?: string | null;
    has_more?: boolean;
  };
}

export interface ReportingApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
  meta?: { request_id?: string };
}

export interface ReportingApiMetaCampaign {
  campaign_id: string;
}

export interface ReportingApiMetaAccount {
  source: string;
  account_id: string;
  campaigns: ReportingApiMetaCampaign[];
}

export interface ReportingApiReportQuery {
  dataset?: ReportingApiDataset;
  dimensions?: string[];
  metrics?: string[];
  filters?: Record<string, string[]>;
  start_date: string;
  end_date: string;
  sort?: string[];
  limit?: number;
  cursor?: string | null;
  extClientId?: string;
}

export type ReportingApiRow = Record<string, string | number | null | undefined>;

export interface ReportingApiV1SelectionFilters {
  source: string;
  accountIds: Array<string | number>;
  campaignIds?: Array<string | number>;
  lineItemIds?: Array<string | number>;
  startDate: string;
  endDate: string;
  extClientId?: string;
}

/** UI-facing shapes adapted from `/api/v1` (kept local — not shared with legacy reporting.types). */

export interface ReportingApiV1MetricSlice {
  impressions: number;
  clicks: number;
  views: number;
  spend: number;
  conversions: number;
  reach?: number;
  results?: number | null;
  ctr: number;
  cpc: number;
  cpm: number;
  cpa: number;
  video_starts: number;
  video_completes: number;
  audio_starts: number;
  audio_completes: number;
  start_date?: string;
  end_date?: string;
  [key: string]: number | string | boolean | null | undefined;
}

export interface ReportingApiV1ReportMetrics {
  current: ReportingApiV1MetricSlice;
  past: ReportingApiV1MetricSlice;
  dif: ReportingApiV1MetricSlice;
}

export interface ReportingApiV1TimeSeriesPoint {
  date: string;
  impressions: number;
  clicks: number;
  views: number;
  interactions: number;
  spend: number;
  conversions: number;
  video_starts: number;
  video_completes: number;
  audio_starts: number;
  audio_completes: number;
  ctr?: number;
  cpm?: number;
  cpa?: number;
  [key: string]: string | number | undefined;
}

export interface ReportingApiV1TablePoint {
  key: string;
  zipcode?: string;
  domain?: string;
  app?: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cpa: number;
  [key: string]: string | number | undefined;
}

export interface ReportingApiV1BreakdownRow {
  date: string;
  campaign_name: string;
  campaign_id: number;
  line_item_name: string | null;
  line_item_id: number | null;
  creative_name: string | null;
  creative_id: number | null;
  media?: string | null;
  impressions: number;
  clicks: number;
  views: number;
  interactions: number;
  spend: number;
  conversions: number;
  video_starts: number;
  video_completes: number;
  audio_starts: number;
  audio_completes: number;
  [key: string]: string | number | null | undefined;
}

export const REPORTING_API_V1_BASE_METRICS = [
  'impressions',
  'clicks',
  'conversions',
  'spend',
  'ctr',
  'cpm',
  'cpc',
  'cpa',
] as const;

/** Performance metrics used for completion-rate cards (API names). */
export const REPORTING_API_V1_COMPLETION_METRICS = [
  'video_starts',
  'video_completions',
  'audio_starts',
  'audio_completes',
] as const;

export const REPORTING_API_V1_EMPTY_MESSAGE = 'No data could be found';

/** Beacon / conversion-action row from dataset=conversions. */
export interface ReportingApiV1ConversionsByBeaconRow {
  conversion_id: string;
  conversion_name: string | null;
  conversion_type: string | null;
  conversions: number;
  conversion_value: number;
  view_through_conversions: number;
}

export type ReportingApiV1FlightEntityLevel = 'campaign' | 'line' | 'line_item' | 'package';

export interface ReportingApiV1FlightsRequest {
  source: string;
  accountIds: Array<string | number>;
  campaignIds?: Array<string | number>;
  entityLevel?: ReportingApiV1FlightEntityLevel;
  asOf?: string;
  extClientId?: string;
}

/** Flight row from shaped GET/POST /api/v1/reports/flights. */
export interface ReportingApiV1FlightRow {
  date?: string;
  source?: string;
  advertiser_id?: string | number;
  advertiser_name?: string | null;
  campaign_id?: string | number | null;
  campaign_name?: string | null;
  package_id?: string | number | null;
  package_name?: string | null;
  line_item_id?: string | number | null;
  line_item_name?: string | null;
  entity_level?: string;
  entity_id?: string | number;
  entity_name?: string | null;
  budget_schedule_id?: string | number | null;
  start_date?: string | null;
  end_date?: string | null;
  budget?: number | string | null;
  daily_budget?: number | string | null;
  budget_type?: string | null;
  daily_budget_type?: string | null;
  dates_derived?: boolean | null;
  budget_derived?: boolean | null;
  daily_budget_derived?: boolean | null;
  currency?: string | null;
  status?: string | null;
  status_derived?: boolean | null;
  [key: string]: string | number | boolean | null | undefined;
}
